import { useState } from 'react';
import { Link } from 'react-router-dom';
import { mutate } from 'swr';

import useToast from '@/components/Toast/useToast';
import { createDataset } from '@/data/mutations';
import {
  invalidateWorkspaceStatsIfNeeded,
  matchKeyPartial,
  useBulkExampleCreate,
  useOrganizationId,
} from '@/hooks/useSwr';
import { DatasetSchema, ExampleSchemaOrDraft } from '@/types/schema';
import {
  apiDatasetsPath,
  apiExamplesPath,
  appDatasetsPath,
  appOrganizationPath,
} from '@/utils/constants';
import { createName } from '@/utils/create-name';

export const useCreateDatasetWithExamples = ({
  description = '',
  examples,
  onSuccess,
}: {
  description?: string;
  examples: ExampleSchemaOrDraft[];
  onSuccess?: (dataset: DatasetSchema) => void;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const organizationId = useOrganizationId();
  const { createToast } = useToast();
  const datasetDataType = 'kv';
  const { trigger: addExamples } = useBulkExampleCreate({ datasetDataType });

  const saveDatasetAndAddExamples = async (datasetName?: string) => {
    if (!organizationId) {
      createToast({
        title: 'Error',
        description: 'Organization not found',
      });
      return;
    }

    try {
      setIsLoading(true);

      // create the dataset
      const dataset = await createDataset(organizationId, {
        name: datasetName || createName('ds'),
        description,
        data_type: datasetDataType,
        extra: {
          source: 'playground',
        },
      });

      // add the examples
      await addExamples({
        dataset_id: dataset.id,
        examples: examples.map((example) => ({
          inputs: JSON.stringify(example.inputs),
          outputs: JSON.stringify(example.outputs),
          metadata: JSON.stringify(example.metadata),
        })),
      });

      onSuccess?.(dataset);

      createToast({
        title: 'Dataset created',
        description: datasetCreatedDescription(dataset, organizationId),
      });
      mutate(matchKeyPartial({ url: `${apiExamplesPath}` }));

      // Invalidate workspace stats after successful dataset creation
      invalidateWorkspaceStatsIfNeeded(apiDatasetsPath, 'POST');

      return dataset;
    } catch (error: unknown) {
      createToast({
        title: 'Failed to create dataset',
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    saveDataset: saveDatasetAndAddExamples,
  };
};

const datasetCreatedDescription = (
  dataset: DatasetSchema,
  organizationId: string
) => {
  return (
    <div>
      Successfully created new dataset{' '}
      <Link
        className="font-bold text-brand-green-400 underline"
        to={`/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${dataset.id}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {dataset.name}
      </Link>{' '}
      and added the examples.
    </div>
  );
};
